import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Users as UsersIcon, 
  UserPlus, 
  Edit, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  Key,
  RefreshCw,
  Search,
  CheckSquare,
  Square,
  UserCheck,
  UserX,
  Shield,
  Upload
} from 'lucide-react';
import Pagination from '@/components/Pagination';
import CustomCheckbox from '@/components/Checkbox';
import BulkActionsToolbar from '@/components/BulkActionsToolbar';
import { usePermissions } from '@/contexts/PermissionsContext';
import { ProtectedButton, ProtectedSection } from '@/components/rbac';

const Users = () => {
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Roles disponibles (système + custom)
  const [availableRoles, setAvailableRoles] = useState([]);
  
  // Bulk selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRoleDialogOpen, setBulkRoleDialogOpen] = useState(false);
  const [bulkRole, setBulkRole] = useState('Operateur');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  
  // Forms
  const [createForm, setCreateForm] = useState({
    nom: '',
    email: '',
    password: '',
    role: 'Operateur'
  });
  
  const [editForm, setEditForm] = useState({
    nom: '',
    email: '',
    role: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [page, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/users/management?${params.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error(formatErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await api.get('/rbac/roles');
      // Combiner les rôles système et personnalisés
      const systemRoles = response.data.system_roles.map(r => ({ name: r.name, type: 'system' }));
      const customRoles = response.data.custom_roles.map(r => ({ name: r.name, type: 'custom' }));
      setAvailableRoles([...systemRoles, ...customRoles]);
    } catch (error) {
      console.error('Erreur lors du chargement des rôles:', error);
      // Fallback vers les rôles par défaut si l'API échoue
      setAvailableRoles([
        { name: 'SuperAdmin', type: 'system' },
        { name: 'Admin', type: 'system' },
        { name: 'Manager', type: 'system' },
        { name: 'Operateur', type: 'system' },
        { name: 'Finance', type: 'system' },
        { name: 'Auditeur', type: 'system' }
      ]);
    }
  };

  // ============ BULK ACTIONS ============
  
  const handleSelectAll = () => {
    if (selectedIds.length === users.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(users.map(u => u.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Aucun utilisateur sélectionné');
      return;
    }

    try {
      await Promise.all(
        selectedIds.map(id => api.put(`/users/${id}/toggle-status`, { is_active: true }))
      );
      toast.success(`${selectedIds.length} utilisateur(s) activé(s)`);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.length === 0) {
      toast.error('Aucun utilisateur sélectionné');
      return;
    }

    try {
      await Promise.all(
        selectedIds.map(id => api.put(`/users/${id}/toggle-status`, { is_active: false }))
      );
      toast.success(`${selectedIds.length} utilisateur(s) désactivé(s)`);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleBulkChangeRole = async () => {
    if (selectedIds.length === 0) {
      toast.error('Aucun utilisateur sélectionné');
      return;
    }

    try {
      await Promise.all(
        selectedIds.map(id => 
          api.put(`/users/${id}`, { role: bulkRole })
        )
      );
      toast.success(`Rôle changé pour ${selectedIds.length} utilisateur(s)`);
      setBulkRoleDialogOpen(false);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('Aucun utilisateur sélectionné');
      return;
    }

    try {
      await Promise.all(
        selectedIds.map(id => api.delete(`/users/${id}`))
      );
      toast.success(`${selectedIds.length} utilisateur(s) supprimé(s)`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds([]);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  // ============ IMPORT MULTIPLE USERS ============
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportFile(file);
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        if (file.name.endsWith('.json')) {
          const data = JSON.parse(event.target.result);
          setImportPreview(Array.isArray(data) ? data : [data]);
        } else if (file.name.endsWith('.csv')) {
          const text = event.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length === 0) {
            toast.error('Fichier CSV vide');
            return;
          }
          
          const headers = lines[0].split(',').map(h => h.trim());
          console.log('CSV Headers:', headers);
          
          const data = lines.slice(1).map((line, idx) => {
            // Better CSV parsing that handles quotes
            const values = line.split(',').map(v => v.trim().replace(/^["']|["']$/g, ''));
            const obj = {};
            headers.forEach((header, i) => {
              obj[header] = values[i] || '';
            });
            console.log(`CSV Row ${idx + 1}:`, obj);
            return obj;
          }).filter(obj => obj.nom || obj.email); // Filter empty rows
          
          console.log('CSV Parsed Data:', data);
          setImportPreview(data);
          
          if (data.length === 0) {
            toast.error('Aucune donnée valide trouvée dans le CSV');
          } else {
            toast.success(`${data.length} utilisateur(s) détecté(s) dans le fichier`);
          }
        }
      } catch (error) {
        console.error('Erreur parsing:', error);
        toast.error('Erreur de lecture du fichier: ' + error.message);
      }
    };

    reader.readAsText(file);
  };

  const handleImportUsers = async () => {
    if (importPreview.length === 0) {
      toast.error('Aucun utilisateur à importer');
      return;
    }

    console.log('Starting import for', importPreview.length, 'users');
    
    try {
      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const userData of importPreview) {
        try {
          const payload = {
            nom: userData.nom || userData.name,
            email: userData.email,
            password: userData.password || 'ChangeMe123!',
            role: userData.role || 'Operateur'
          };
          
          console.log('Creating user:', payload);
          const response = await api.post('/users/create', payload);
          console.log('User created:', response.data);
          successCount++;
        } catch (error) {
          errorCount++;
          const errorMsg = error.response?.data?.detail || error.message;
          errors.push(`${userData.email}: ${errorMsg}`);
          console.error(`Erreur pour ${userData.email}:`, error.response?.data || error.message);
        }
      }

      console.log(`Import completed: ${successCount} success, ${errorCount} errors`);
      
      if (successCount > 0) {
        toast.success(`${successCount} utilisateur(s) créé(s)${errorCount > 0 ? `, ${errorCount} erreur(s)` : ''}`);
      }
      
      if (errorCount > 0) {
        toast.error(`Erreurs: ${errors.join(', ')}`);
      }
      
      setImportDialogOpen(false);
      setImportFile(null);
      setImportPreview([]);
      fetchUsers();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Erreur lors de l\'import: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateUser = async () => {
    if (!createForm.nom || !createForm.email || !createForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    try {
      await api.post('/users/create', createForm);
      toast.success('Utilisateur créé avec succès');
      setCreateDialogOpen(false);
      setCreateForm({ nom: '', email: '', password: '', role: 'Operateur' });
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditForm({
      nom: user.nom,
      email: user.email,
      role: user.role
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editForm.nom || !editForm.email) {
      toast.error('Nom et email requis');
      return;
    }
    
    try {
      const roleChanged = editForm.role && editForm.role !== editingUser.role;
      
      await api.put(`/users/${editingUser.id}`, editForm);
      
      if (roleChanged) {
        toast.success(
          'Utilisateur mis à jour. ⚠️ L\'utilisateur doit se reconnecter pour que le nouveau rôle soit appliqué.',
          { duration: 8000 }
        );
      } else {
        toast.success('Utilisateur mis à jour');
      }
      
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus === 'active' ? 'désactiver' : 'activer';
    if (!confirm(`Voulez-vous ${action} cet utilisateur?`)) return;
    
    try {
      const response = await api.put(`/users/${userId}/toggle-status`);
      toast.success(`Utilisateur ${response.data.status === 'active' ? 'activé' : 'désactivé'}`);
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await api.post(`/users/${userId}/reset-password`);
      setResetPasswordDialog({
        userId,
        tempPassword: response.data.temporary_password
      });
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Supprimer cet utilisateur? Cette action est définitive.')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Utilisateur supprimé');
      fetchUsers();
    } catch (error) {
      toast.error(formatErrorMessage(error));
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Admin':
        return 'bg-purple-100 text-purple-800';
      case 'Operateur':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">Actif</span>;
    }
    return <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">Inactif</span>;
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <UsersIcon className="h-8 w-8" />
          Gestion des Utilisateurs
        </h1>
        <p className="text-gray-600">Gérez les comptes et les rôles des utilisateurs</p>
      </div>

      {/* Filters & Actions */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={roleFilter || "tous"} onValueChange={(value) => setRoleFilter(value === "tous" ? "" : value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tous les rôles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les rôles</SelectItem>
                {availableRoles.map((role) => (
                  <SelectItem key={role.name} value={role.name}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter || "tous"} onValueChange={(value) => setStatusFilter(value === "tous" ? "" : value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tous les statuts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tous">Tous les statuts</SelectItem>
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="inactive">Inactif</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={fetchUsers} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
            
            <ProtectedButton 
              resource="users" 
              action="create"
              onClick={() => setImportDialogOpen(true)} 
              variant="outline" 
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importer Utilisateurs
            </ProtectedButton>
            
            <ProtectedButton 
              resource="users" 
              action="create"
              onClick={() => setCreateDialogOpen(true)} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Nouvel Utilisateur
            </ProtectedButton>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Toolbar */}
      {selectedIds.length > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedIds.length}
          totalCount={users.length}
          onSelectAll={handleSelectAll}
          onDeselectAll={() => setSelectedIds([])}
          onBulkDelete={() => setBulkDeleteDialogOpen(true)}
          entityName="utilisateur"
          customActions={[
            {
              label: `Activer (${selectedIds.length})`,
              icon: <UserCheck className="h-4 w-4" />,
              onClick: handleBulkActivate,
              variant: 'default'
            },
            {
              label: `Désactiver (${selectedIds.length})`,
              icon: <UserX className="h-4 w-4" />,
              onClick: handleBulkDeactivate,
              variant: 'outline'
            },
            {
              label: `Changer rôle (${selectedIds.length})`,
              icon: <Shield className="h-4 w-4" />,
              onClick: () => setBulkRoleDialogOpen(true),
              variant: 'outline'
            },
            {
              label: `Supprimer (${selectedIds.length})`,
              icon: <Trash2 className="h-4 w-4" />,
              onClick: () => setBulkDeleteDialogOpen(true),
              variant: 'outline'
            }
          ]}
        />
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des Utilisateurs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 w-12">
                        <CustomCheckbox
                          checked={selectedIds.length === users.length && users.length > 0}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="text-left py-3 px-4">Nom</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4">Rôle</th>
                      <th className="text-left py-3 px-4">Statut</th>
                      <th className="text-left py-3 px-4">Dernière connexion</th>
                      <th className="text-right py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <CustomCheckbox
                            checked={selectedIds.includes(user.id)}
                            onChange={() => handleSelectOne(user.id)}
                          />
                        </td>
                        <td className="py-3 px-4 font-medium">{user.nom}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">{user.email}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(user.status)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : 'Jamais'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(user)}
                              title="Modifier"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleStatus(user.id, user.status)}
                              title={user.status === 'active' ? 'Désactiver' : 'Activer'}
                            >
                              {user.status === 'active' ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-gray-400" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResetPassword(user.id)}
                              title="Réinitialiser mot de passe"
                            >
                              <Key className="h-4 w-4 text-orange-600" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(user.id)}
                              title="Supprimer"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un Utilisateur</DialogTitle>
            <DialogDescription>
              Ajoutez un nouvel utilisateur au système
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom complet*</Label>
              <Input
                id="nom"
                value={createForm.nom}
                onChange={(e) => setCreateForm({...createForm, nom: e.target.value})}
                placeholder="Jean Dupont"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email*</Label>
              <Input
                id="email"
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                placeholder="jean@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe*</Label>
              <Input
                id="password"
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Rôle*</Label>
              <Select
                value={createForm.role}
                onValueChange={(value) => setCreateForm({...createForm, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.name} value={role.name}>
                      {role.name} {role.type === 'custom' && '(Personnalisé)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateUser} data-testid="user-create-submit-btn">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'Utilisateur</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-nom">Nom complet*</Label>
              <Input
                id="edit-nom"
                value={editForm.nom}
                onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email*</Label>
              <Input
                id="edit-email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({...editForm, email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-role">Rôle*</Label>
              <Select
                value={editForm.role}
                onValueChange={(value) => setEditForm({...editForm, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.name} value={role.name}>
                      {role.name} {role.type === 'custom' && '(Personnalisé)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateUser}>
              Mettre à jour
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPasswordDialog} onOpenChange={() => setResetPasswordDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mot de Passe Réinitialisé</DialogTitle>
            <DialogDescription>
              Copiez ce mot de passe temporaire et communiquez-le à l'utilisateur
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 mb-2">⚠️ Ce mot de passe ne sera affiché qu'une seule fois</p>
              <div className="flex items-center gap-2">
                <Input
                  value={resetPasswordDialog?.tempPassword || ''}
                  readOnly
                  className="font-mono font-bold"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(resetPasswordDialog?.tempPassword || '');
                    toast.success('Mot de passe copié');
                  }}
                >
                  Copier
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setResetPasswordDialog(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Bulk Change Role Dialog */}
      <Dialog open={bulkRoleDialogOpen} onOpenChange={setBulkRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Changer le rôle de {selectedIds.length} utilisateur(s)</DialogTitle>
            <DialogDescription>
              Sélectionnez le nouveau rôle à appliquer
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="bulk-role">Nouveau rôle</Label>
              <Select value={bulkRole} onValueChange={setBulkRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.name} value={role.name}>
                      {role.name} {role.type === 'custom' && '(Personnalisé)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRoleDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleBulkChangeRole}>
              Changer le rôle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer {selectedIds.length} utilisateur(s) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete}>
              Supprimer {selectedIds.length} utilisateur(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Users Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Importer plusieurs utilisateurs</DialogTitle>
            <DialogDescription>
              Importez des utilisateurs depuis un fichier CSV ou JSON
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="import-file">Fichier (CSV ou JSON)</Label>
              <Input
                id="import-file"
                type="file"
                accept=".csv,.json"
                onChange={handleFileUpload}
              />
              <p className="text-xs text-gray-500">
                Format CSV: nom,email,password,role<br />
                Format JSON: [{"{"}nom,email,password,role{"}"}]
              </p>
            </div>

            {importPreview.length > 0 && (
              <div className="space-y-2">
                <Label>Aperçu ({importPreview.length} utilisateur(s))</Label>
                <div className="max-h-60 overflow-auto border rounded p-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1 px-2">Nom</th>
                        <th className="text-left py-1 px-2">Email</th>
                        <th className="text-left py-1 px-2">Rôle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.slice(0, 10).map((user, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-1 px-2">{user.nom || user.name || '-'}</td>
                          <td className="py-1 px-2">{user.email || '-'}</td>
                          <td className="py-1 px-2">{user.role || 'Operateur'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importPreview.length > 10 && (
                    <p className="text-xs text-gray-500 mt-2">
                      ... et {importPreview.length - 10} autre(s)
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setImportDialogOpen(false);
              setImportFile(null);
              setImportPreview([]);
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleImportUsers}
              disabled={importPreview.length === 0}
            >
              Importer {importPreview.length} utilisateur(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
